import {
    buildSupabaseChainFilter,
    convertKeysToCamelCase,
    convertTutorSnakeToCamelCase,
    handlePageCountCommon,
} from 'utils/utilCommon'
import { decryptData } from 'utils/crypto'
import { supabase } from '../axios.instance'
import { createSupabaseQuery } from '../common'
import { getTutorCountRPC } from '../rpc/rpc.api'

const table = 'tutor_info'
const tableMatching = 'program_instructor_matching'


/**
 * 강사 목록
 * @param search
 * @param regionId
 * @param path
 * @returns {Promise<{data}|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>|null>}
 */
export const fetchTutorList = async (search, regionId, path) => {
    try {
        const pageSize = 10
        // query
        const { query } = await buildSupabaseChainFilter(table, search, regionId, path)

        const response = await query
        const searchParams = new URLSearchParams(search)
        const queryParams = new URLSearchParams(query.url.search)
        const params = Object.fromEntries(queryParams.entries())

        const newParams = {
            region_id: params.region_id,
            is_youth: params.is_youth,
            is_retiree: params.is_retiree,
            is_rehired: params.is_rehired,
            is_advanced_instructor: params.is_advanced_instructor,
            is_employment: params.is_employment,
            is_foreign: params.is_foreign,
            has_disability: params.has_disability,
            is_veterans: params.is_veterans,
            education_level: params.education_level,
            teacher_certification: params.teacher_certification,
            teaching_experience_yn: params.teaching_experience_yn,
            is_language: params.is_language,
            use_language: params.use_language,
            employment_status: params.employment_status,
            employment_type: searchParams.get('employmentType'),
            start_date: searchParams.get('startDate'),
            end_date: searchParams.get('endDate'),
        }

        // 필터 카운트 정보 response
        let filter = await getTutorCountRPC(newParams)
        filter = convertKeysToCamelCase(filter)
        // 페이지 정보
        const paginationInfo = handlePageCountCommon(search, response, pageSize)

        //현재 JSON 데이터 형태로 컨버팅
        if (response.data && Array.isArray(response.data)) {
            if (path === 'data-table') {
                return response
            }

            // snake → camel 변환
            const convertedData = response.data.map(tutor => {
                return convertTutorSnakeToCamelCase(tutor)
            })

            response.data = {
                filterCountInfo: filter,
                tutorInfoList: convertedData,
                page: paginationInfo,
            }

            return response
        }

        return response
    } catch (error) {
        console.error('fetchTutorList error:', error)
        return {
            data: {
                filterCountInfo: {},
                tutorInfoList: [],
                page: { totalCount: 0, totalPages: 0, currentPage: 1 },
            },
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 교육관리 강사매칭 강사목록
 * 등록/수정 페이지
 * @param search
 * @param regionId
 * @param path
 * @returns {Promise<{data}|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>|null>}
 */
export const fetchMatchTutorList = async (search, regionId) => {
    try {
        const tutorId = 'tutor_id'
        let query = createSupabaseQuery(table, regionId)
        query = query.order(tutorId, { ascending: false })
        const response = await query

        //현재 JSON 데이터 형태로 컨버팅
        if (response.data && Array.isArray(response.data)) {
            // 변환
            const convertedData = response.data.map(convertTutorSnakeToCamelCase)

            response.data = {
                tutorInfoList: convertedData,
            }
            return response
        }
        return response
    } catch (error) {
        console.error('fetchMatchTutorList error:', error)
        return {
            data: { tutorInfoList: [] },
            error: error.message,
            status: 'error',
        }
    }
}

export const getTutorsById = async tutorId => {
    try {
        if (!tutorId) {
            throw new Error('Tutor ID is required')
        }

        const response = await supabase.from(table).select(`*,program_instructor_matching(*)`).eq('tutor_id', tutorId)

        if (response.data && Array.isArray(response.data)) {
            // 변환
            const convertedData = response.data.map(tutor => {
                return convertTutorSnakeToCamelCase(tutor)
            })

            return convertedData.length > 0 ? convertedData[0] : null
        }

        return null
    } catch (error) {
        console.error('getTutorsById error:', error)
        return null
    }
}

export const postTutors = async tutorList => {
    try {
        // tutorList가 null, undefined이거나 빈 배열/객체인 경우 체크
        if (
            !tutorList ||
            (Array.isArray(tutorList) && tutorList.length === 0) ||
            (typeof tutorList === 'object' && !Array.isArray(tutorList) && Object.keys(tutorList).length === 0)
        ) {
            throw new Error('Valid tutor list is required')
        }

        const response = await supabase.from(table).insert(tutorList).select()

        if (response.data && Array.isArray(response.data)) {
            // 원래 입력이 단건(객체)이었다면 첫 번째 요소만 반환
            if (!Array.isArray(tutorList)) {
                const [firstItem] = response.data
                response.data = firstItem
            }
        }

        return response
    } catch (error) {
        console.error('postTutors error:', error)
        return {
            data: null,
            error: error.message,
            status: 'error',
        }
    }
}

export const editTutor = async tutor => {
    try {
        if (!tutor || !tutor.tutor_id) {
            throw new Error('Valid tutor data with tutor_id is required')
        }

        const response = await supabase.from(table).update(tutor).eq('tutor_id', tutor.tutor_id).select()

        return response
    } catch (error) {
        console.error('editTutor error:', error)
        return {
            data: null,
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 수퍼베이스 삭제
 * is_delete(삭제여부) true로 변경
 * @param tutorId
 * @returns {Promise<*|null>}
 */
export const deleteTutorById = async tutorId => {
    try {
        if (!tutorId) {
            throw new Error('Tutor ID is required')
        }

        const [response] = await Promise.all([
            supabase.from(table).update({ is_delete: true }).eq('tutor_id', tutorId).select(),
            supabase.from(tableMatching).update({ is_delete: true }).eq('tutor_id', tutorId).select(),
        ])

        return response
    } catch (error) {
        console.error('deleteTutorById error:', error)
        return {
            data: null,
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * DB에서 같은 지역 강사 동명이인 찾기 (배열)
 * 암호화된 데이터와 평문 데이터를 모두 검색
 * @param tutorNames
 * @param regionId
 * @param tutorYearArray
 * @returns {Promise<*|({error: true} & "Received a generic string")>}
 */
export const findDuplicatedTutorNames = async (tutorNames, regionId, tutorYearArray) => {
    try {
        if (!Array.isArray(tutorNames) || tutorNames.length === 0) {
            return { data: [] }
        }

        const batchSize = 100

        // 평문으로 검색
        const batches = []
        for (let i = 0; i < tutorNames.length; i += batchSize) {
            batches.push(tutorNames.slice(i, i + batchSize))
        }

        const promises = batches.map(async batch => {
            let query = createSupabaseQuery(table, regionId)
            query = query.select().in('tutor_name', batch)

            if (Array.isArray(tutorYearArray) && tutorYearArray.length > 0) {
                query = query.in('registration_year', tutorYearArray)
            }

            const response = await query

            if (response.data && Array.isArray(response.data)) {
                return response.data.map(convertTutorSnakeToCamelCase)
            }
            return []
        })

        const results = await Promise.all(promises)
        const combinedResults = results.flat()
        const uniqueResults = combinedResults.reduce((acc, current) => {
            const existing = acc.find(item => item.tutorId === current.tutorId)
            if (!existing) {
                acc.push(current)
            }
            return acc
        }, [])

        return { data: uniqueResults }
    } catch (error) {
        console.error('Error finding duplicated tutor names:', error)
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * DB에서 같은 지역 강사 동명이인 찾기 (단건 등록)
 * 암호화된 데이터와 평문 데이터를 모두 검색
 * @param tutorName
 * @param regionId
 * @param year
 * @returns {Promise<*|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>>}
 */
export const findDuplicatedTutorName = async (tutorName, regionId, year) => {
    try {
        if (!tutorName) {
            return { data: [] }
        }

        // 평문으로 검색
        let query = createSupabaseQuery(table, regionId)
        query = query.select().eq('tutor_name', tutorName)

        if (year) {
            query = query.eq('registration_year', year)
        }

        const response = await query

        // 결과 합치기
        const combinedData = []

        if (response.data && Array.isArray(response.data)) {
            combinedData.push(...response.data.map(convertTutorSnakeToCamelCase))
        }

        // 중복 제거 (tutorId 기준)
        const uniqueData = combinedData.reduce((acc, current) => {
            const existing = acc.find(item => item.tutorId === current.tutorId)
            if (!existing) {
                acc.push(current)
            }
            return acc
        }, [])

        return {
            data: uniqueData,
            error: null,
            status: 'success',
        }
    } catch (error) {
        console.error('findDuplicatedTutorName error:', error)
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 교육관리 강사매칭 강사목록 상세
 * @param id
 * @returns {Promise<*|PostgrestResponseSuccess<GetResult<any, any, R | unknown, "*">[]>|PostgrestResponseFailure>}
 */
export const handleGetTutor = async id => {
    try {
        if (!id) {
            throw new Error('Tutor ID is required')
        }

        let query = supabase.from(table).select('*', { count: 'exact' })
        query = query.eq('is_delete', false)
        query = query.eq('tutor_id', id)

        const result = await query


        return result
    } catch (error) {
        console.error('handleGetTutor error:', error)
        // 오류 객체 대신 구조화된 응답 반환
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 월별 강사정보 요약 통계 조회 (chart 용)
 * @param month 1~12월 (숫자)
 */
export const getMonthlyTutorChart = async month => {
    try {
        if (!month || month < 1 || month > 12) {
            throw new Error('Valid month (1-12) is required')
        }

        const { data, error } = await supabase.rpc('get_month_total_tutor', {
            month_param: month,
        })

        if (error) {
            console.error('get_month_total_tutor 에러:', error.message)
            return {
                data: [],
                error: error.message,
                status: 'error',
            }
        }

        return data ?? []
    } catch (error) {
        console.error('getMonthlyTutorChart 호출 실패:', error)
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

export const getTutorHistory = async tutorId => {
    try {
        const { data, error } = await supabase.rpc('get_audit_log', {
            category_param: 'tutor',
            id_param: tutorId,
        })

        if (error) {
            console.error('getTutorHistory 호출 실패:', error)
            return []
        }

        return data
    } catch (error) {
        console.error('getTutorHistory 호출 실패:', error)
        return []
    }
}


/**
 * 전체 강사들의 민감정보를 복호화하여 평문으로 저장
 * 상용 테이블의 암호화 데이터를 평문으로 되돌리는 용도
 * 대량 데이터 처리를 위해 배치 처리 방식 사용
 * @param {Object} options - 옵션 객체
 * @param {number} options.batchSize - 배치 크기 (기본값: 50)
 * @param {number} options.batchDelay - 배치 간 딜레이(ms) (기본값: 100)
 * @param {Function} options.onProgress - 진행 상황 콜백 (processed, total) => void
 * @returns {Promise<{success: boolean, processed: number, errors: Array}>}
 */
export const decryptTutorsToPlaintext = async (options = {}) => {
    const { batchSize = 50, batchDelay = 100, onProgress } = options

    try {
        // 전체 데이터 조회 (페이지네이션으로 모든 데이터 가져오기)
        console.log('🔍 test_tutor_info 테이블 조회 시작...')
        
        // 먼저 전체 개수 확인
        const { count, error: countError } = await supabase
            .from(table)
            .select('*', { count: 'exact', head: true })

        if (countError) {
            console.error('전체 개수 조회 실패:', countError)
            return {
                success: false,
                processed: 0,
                errors: [countError.message],
            }
        }

        console.log(`📊 전체 데이터 개수: ${count}건`)

        if (!count || count === 0) {
            console.warn('⚠️ 조회된 데이터가 없습니다.')
            return {
                success: true,
                processed: 0,
                errors: [],
                message: '처리할 데이터가 없습니다.',
            }
        }

        // 페이지네이션으로 모든 데이터 가져오기 (Supabase는 기본적으로 1000건 제한)
        const pageSize = 1000
        const totalPages = Math.ceil(count / pageSize)

        console.log(`📦 ${totalPages}개 페이지로 나눠서 데이터를 가져옵니다...`)

        // 각 페이지를 순차적으로 가져오기 (reduce 사용)
        const pageIndices = Array.from({ length: totalPages }, (_, i) => i)
        
        const fetchPage = async (pageIndex) => {
            const from = pageIndex * pageSize
            const to = Math.min(from + pageSize - 1, count - 1)

            const { data: pageData, error: pageError } = await supabase
                .from(table)
                .select('*')
                .range(from, to)

            if (pageError) {
                console.error(`페이지 ${pageIndex + 1} 조회 실패:`, pageError)
                throw new Error(`페이지 ${pageIndex + 1} 조회 실패: ${pageError.message}`)
            }

            if (pageData && pageData.length > 0) {
                console.log(`✅ 페이지 ${pageIndex + 1}/${totalPages} 완료 (${pageData.length}건)`)
                return pageData
            }

            return []
        }

        // 순차적으로 모든 페이지 가져오기
        const allTutors = await pageIndices.reduce(
            async (previousPromise, pageIndex) => {
                const accumulated = await previousPromise
                const pageData = await fetchPage(pageIndex)
                return [...accumulated, ...pageData]
            },
            Promise.resolve([]),
        )

        console.log(`🔍 전체 조회 완료: 총 ${allTutors.length}건`)

        if (allTutors.length === 0) {
            console.warn('⚠️ 조회된 데이터가 없습니다. 가능한 원인:')
            console.warn('1. RLS (Row Level Security) 정책이 적용되어 있을 수 있습니다.')
            console.warn('2. 테이블 권한이 없을 수 있습니다.')
            console.warn('3. 실제로 데이터가 없을 수 있습니다.')
            
            return {
                success: false,
                processed: 0,
                errors: ['조회된 데이터가 없습니다. RLS 정책 또는 권한을 확인해주세요.'],
                count,
            }
        }

        const tutors = allTutors
        const total = tutors.length
        const errors = []

        // 배치로 나눠서 처리
        const batches = []
        for (let i = 0; i < tutors.length; i += batchSize) {
            batches.push(tutors.slice(i, i + batchSize))
        }

        console.log(`총 ${total}건의 데이터를 ${batches.length}개 배치로 처리합니다. (배치 크기: ${batchSize})`)

        // 복호화 헬퍼 함수 (이 함수 내부에서만 사용)
        const safeDecryptTutor = (tutor) => {
            if (!tutor) return tutor

            const decryptedTutor = { ...tutor }
            const SENSITIVE_FIELDS = ['tutor_name', 'resident_num', 'contact', 'email']

            SENSITIVE_FIELDS.forEach(field => {
                if (tutor[field]) {
                    const decryptedValue = decryptData(tutor[field])
                    // null이 아니면 사용, null이면 빈 문자열
                    decryptedTutor[field] = decryptedValue ?? ''
                } else {
                    decryptedTutor[field] = ''
                }
            })

            return decryptedTutor
        }

        // 배치 처리 함수
        const processBatch = async batch => {
            // 배치 내에서는 병렬 처리
            const batchResults = await Promise.all(
                batch.map(async tutor => {
                    try {
                        // 민감정보 복호화 (이미 평문이면 그대로 반환됨)
                        const decryptedTutor = safeDecryptTutor(tutor)

                        // 평문으로 저장할 데이터 준비
                        const { tutor_id, ...updateData } = decryptedTutor

                        // 민감정보 필드만 평문으로 업데이트
                        const plaintextUpdate = {}
                        const SENSITIVE_FIELDS = ['tutor_name', 'resident_num', 'contact', 'email']
                        SENSITIVE_FIELDS.forEach(field => {
                            if (updateData[field] !== undefined) {
                                plaintextUpdate[field] = updateData[field]
                            }
                        })

                        // 데이터베이스에 평문으로 저장
                        const { error: updateError } = await supabase
                            .from(table)
                            .update(plaintextUpdate)
                            .eq('tutor_id', tutor.tutor_id)

                        if (updateError) {
                            console.error(`강사 ID ${tutor.tutor_id} 업데이트 실패:`, updateError)
                            return {
                                success: false,
                                tutor_id: tutor.tutor_id,
                                tutor_name: decryptedTutor.tutor_name,
                                error: updateError.message,
                            }
                        }

                        return {
                            success: true,
                            tutor_id: tutor.tutor_id,
                            tutor_name: decryptedTutor.tutor_name,
                        }
                    } catch (error) {
                        console.error(`강사 ID ${tutor.tutor_id} 처리 중 오류:`, error)
                        return {
                            success: false,
                            tutor_id: tutor.tutor_id,
                            tutor_name: tutor.tutor_name || '',
                            error: error.message,
                        }
                    }
                }),
            )

            return batchResults
        }

        // 배치 순차 처리 및 결과 집계 (reduce 사용)
        const delay = ms => new Promise(resolve => {
            setTimeout(() => {
                resolve()
            }, ms)
        })

        const processAllBatches = async () => {
            let processedCount = 0

            const processBatchWithDelay = async (batch, index) => {
                const batchResults = await processBatch(batch)

                // 배치 결과 집계
                batchResults.forEach(result => {
                    if (result.success) {
                        processedCount += 1
                    } else {
                        errors.push(result)
                    }
                })

                // 진행 상황 콜백 호출
                if (onProgress && typeof onProgress === 'function') {
                    onProgress(processedCount, total)
                }

                // 마지막 배치가 아니면 딜레이 추가 (API 부하 방지)
                if (index < batches.length - 1 && batchDelay > 0) {
                    await delay(batchDelay)
                }

                console.log(`배치 ${index + 1}/${batches.length} 완료 (${processedCount}/${total})`)

                return processedCount
            }

            // 각 배치를 순차적으로 처리
            await batches.reduce(
                (previousPromise, batch, index) => previousPromise.then(() => processBatchWithDelay(batch, index)),
                Promise.resolve(),
            )

            return processedCount
        }

        const processedCount = await processAllBatches()

        return {
            success: errors.length === 0,
            processed: processedCount,
            total,
            errors,
        }
    } catch (error) {
        console.error('decryptTutorsToPlaintext 오류:', error)
        return {
            success: false,
            processed: 0,
            errors: [error.message],
        }
    }
}